import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import {
  DEFAULT_LOOP_SECONDS,
  MAX_LOOP_SECONDS,
  MIN_LOOP_SECONDS,
  TICKER_KINDS,
  TICKER_MODES,
  WEATHER_CREDIT,
  WEATHER_VIEWS,
  CORNER_SLOTS,
  SPOTLIGHT_TOUR_GAPS,
  SPOTLIGHT_TOUR_WHO,
  WIDGETS,
  WIDGET_SLOTS,
  WIDGET_SIZES,
  allowedTickerModes,
  backyardAtHour,
  backyardFloorAtHour,
  backyardTotalMeters,
  clockSeconds,
  courseIsFull,
  courseLiveSlot,
  formatClock,
  formatDistance,
  formatTemp,
  gameScoreLabel,
  parseClock,
  parseDistance,
  raceStatusLabel,
  resultsBackyard,
  resultsPaceLive,
  showsRaceList,
  showsSides,
  tourSettings,
  weatherIcon,
  type ClientMessage,
  type CornerSlot,
  type CoursePosition,
  type Participant,
  type RaceEntry,
  type RaceEntryStatus,
  type ResultsBackyard,
  type ResultsStatus,
  type SportId,
  type SpotlightConfig,
  type SpotlightState,
  type SpotlightTour,
  type TickerEntry,
  type TickerEntryKind,
  type TickerMode,
  type TickerState,
  type WeatherState,
  type WidgetConfig,
  type WidgetSize,
  type WidgetSlot,
  type WidgetState,
} from '@rabbitfuel/shared';
import { api } from '../lib/api.ts';
import { timeShort } from '../lib/format.ts';
import { ColorPicker } from './ColorMenu.tsx';
import { TickerClockLabel, TickerFeed, useTickerClock } from './Ticker.tsx';
import { COURSE_PAD, CourseMap, useCoursePositions } from './CourseMap.tsx';
import { courseShape } from '../lib/course.ts';
import { HelpLink } from './DirectorHelp.tsx';
import { keyLabel } from '../lib/shortcuts.ts';
import { SpotlightButton } from './SpotlightButton.tsx';

/**
 * Das Ticker-Pult der Regie.
 *
 * Gepflegt wird von Hand -- ausser auf der Strecke, wenn RACE RESULT laeuft
 * (`results`): dann fuellt die Zeitnahme die Liste und beim Backyard auch
 * Loops und Feld. Der Zuschnitt richtet sich nach dem Modus: bei einem Spiel
 * zaehlen zwei Knoepfe Tore, bei Tennis rechnet der Server aus einem Punkt
 * Spiel und Satz, auf der Strecke pflegt die Regie eine Liste mit Messpunkten.
 */
export function TickerDesk({
  slug,
  sport,
  finished,
  ticker,
  feed,
  widgets,
  weather,
  course,
  results,
  spotlight,
  send,
}: {
  slug: string;
  /** Grenzt die waehlbaren Ticker-Arten ein -- ein Tor-Tracker passt nicht zu einem Lauf. */
  sport: SportId;
  /** Event steht auf „Beendet" -- dann bleibt die Uhr stehen. */
  finished: boolean;
  ticker: TickerState;
  feed: TickerEntry[];
  widgets: WidgetState;
  weather: WeatherState;
  /** Die Strecke des Events (`courseOf`) fuer die Streckenkarte -- null ohne Strecken-Zone. */
  course: [number, number][] | null;
  /** Ergebnisquelle -- fuehrt sie beim Backyard Loops und Feld, zaehlt hier niemand mehr. */
  results: ResultsStatus;
  /** Athleten-Pop-up: wessen gerade laeuft und wo es steht. */
  spotlight: SpotlightState;
  send: (msg: ClientMessage) => void;
}) {
  const [open, setOpen] = useState(true);
  const spotBib = spotlight.active?.bib ?? null;
  const rr = ticker.mode === 'backyard' ? resultsBackyard(results) : NO_RESULTS;

  return (
    <section className={`deck${open ? '' : ' is-collapsed'}`}>
      <header className="deck__head">
        <button type="button" className="deck__toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? '▾' : '▸'} Live-Ticker &amp; Einblendungen
        </button>
        <HelpLink topic="ticker" />
        <span className="deck__mode">{TICKER_MODES.find((m) => m.id === ticker.mode)?.label}</span>
      </header>

      {open && (
        <div className="deck__body">
          <ModePicker sport={sport} ticker={ticker} send={send} />

          {ticker.mode !== 'off' && (
            <>
              {showsSides(ticker.mode, sport) && <SidesPanel ticker={ticker} send={send} />}
              {ticker.mode === 'backyard' && (
                <BackyardPanel ticker={ticker} rr={rr} lastOkAt={results.lastOkAt} send={send} />
              )}
              {/* Auch beim Backyard fuehrt die Regie eine Liste -- wer noch
                  dabei ist und wer ausgestiegen, ist dort die Geschichte. */}
              {showsRaceList(ticker.mode) && <RacePanel slug={slug} ticker={ticker} spotBib={spotBib} send={send} />}
              {ticker.mode === 'backyard' && (
                <CoursePanel
                  slug={slug}
                  ticker={ticker}
                  course={course}
                  results={results}
                  spotBib={spotBib}
                  send={send}
                />
              )}
              <ClockPanel ticker={ticker} finished={finished} rrLoops={rr.loops} send={send} />
              <PostPanel ticker={ticker} feed={feed} send={send} />
            </>
          )}

          <WidgetPanel
            mode={ticker.mode}
            note={ticker.note}
            widgets={widgets}
            spotlight={spotlight}
            send={send}
          />
          <WeatherPanel weather={weather} shown={widgets.weather.on} send={send} />
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- Modus */

function ModePicker({
  sport,
  ticker,
  send,
}: {
  sport: SportId;
  ticker: TickerState;
  send: (m: ClientMessage) => void;
}) {
  const current = TICKER_MODES.find((m) => m.id === ticker.mode);
  // Nur was zur Sportart passt -- ein Tor-Tracker bei einem Lauf waere
  // sinnlos. Der gerade aktive Modus bleibt trotzdem sichtbar, falls die
  // Sportart nachtraeglich geaendert wurde und er dadurch nicht mehr in die
  // Liste faellt -- sonst wuerde die aktuelle Auswahl kommentarlos verschwinden.
  const allowed = new Set(allowedTickerModes(sport));
  allowed.add(ticker.mode);
  const options = TICKER_MODES.filter((m) => allowed.has(m.id));

  return (
    <DeckSection id="mode" title="Ticker-Art" summary={current?.label}>
      <div className="chip-row">
        {options.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`chip${ticker.mode === m.id ? ' is-active' : ''}`}
            title={m.hint}
            onClick={() => send({ t: 'ticker:mode', mode: m.id })}
          >
            {m.label}
          </button>
        ))}
      </div>
      {current && (
        <p className="deck__hint">
          {/* Bei Streckensport bringt „Frei" keine zwei Zaehler mit -- dann
              waere der Standardhinweis schlicht falsch. */}
          {current.id === 'plain' && !showsSides('plain', sport)
            ? 'Freier Ticker mit Uhr — ohne Zähler, weil es bei diesem Sport keine zwei Seiten gibt.'
            : current.hint}
        </p>
      )}
    </DeckSection>
  );
}

/* ------------------------------------------------------- Namen und Stand */

const PERIODS: Record<string, string[]> = {
  match: ['1. Halbzeit', 'Pause', '2. Halbzeit', 'Verlängerung', 'Ende'],
  tennis: ['Satz 1', 'Satz 2', 'Satz 3', 'Satz 4', 'Satz 5'],
  race: ['auf der Strecke', 'Zieleinlauf', 'beendet'],
  plain: ['läuft', 'Pause', 'Ende'],
};

/** Fussballsprache passt nur zum Fussball -- pro Modus die eigenen Worte. */
const SIDE_WORDS = {
  match: { heading: 'Mannschaften', add: '+ Tor', noun: 'Tor', hint: 'Wer hat getroffen? (optional, landet im Ticker)' },
  tennis: { heading: 'Spieler:innen', add: '+ Punkt', noun: 'Punkt', hint: '' },
  plain: { heading: 'Zähler', add: '+ 1', noun: 'Punkt', hint: 'Wofür? (optional, landet im Ticker)' },
} as const;

function SidesPanel({ ticker, send }: { ticker: TickerState; send: (m: ClientMessage) => void }) {
  const tennis = ticker.mode === 'tennis';
  const words = SIDE_WORDS[ticker.mode === 'match' ? 'match' : tennis ? 'tennis' : 'plain'];
  const [note, setNote] = useState('');

  const rename = (i: 0 | 1, value: string) => {
    const sides: [Partial<TickerState['sides'][0]>, Partial<TickerState['sides'][0]>] = [{}, {}];
    sides[i] = { name: value };
    send({ t: 'ticker:board', patch: { sides } });
  };

  const setShort = (i: 0 | 1, value: string) => {
    const sides: [Partial<TickerState['sides'][0]>, Partial<TickerState['sides'][0]>] = [{}, {}];
    sides[i] = { short: value };
    send({ t: 'ticker:board', patch: { sides } });
  };

  const correct = (i: 0 | 1, field: 'score' | 'games', value: number) => {
    const sides: [Partial<TickerState['sides'][0]>, Partial<TickerState['sides'][0]>] = [{}, {}];
    sides[i] = { [field]: value };
    send({ t: 'ticker:board', patch: { sides } });
  };

  return (
    <DeckSection id="sides" title={words.heading}>

      {([0, 1] as const).map((i) => {
        const side = ticker.sides[i];
        return (
          <div className="deck-side" key={i}>
            <input
              className="deck-side__name"
              value={side.name}
              onChange={(e) => rename(i, e.target.value)}
              placeholder={ticker.mode === 'plain' ? `Zähler ${i + 1}` : i === 0 ? 'Heim' : 'Gast'}
              aria-label={`Name ${i === 0 ? 'links' : 'rechts'}`}
            />
            <input
              className="deck-side__short"
              value={side.short}
              onChange={(e) => setShort(i, e.target.value)}
              maxLength={5}
              title="Kürzel für die Einblendung"
              aria-label="Kürzel"
            />

            {tennis ? (
              <>
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={() => send({ t: 'ticker:point', side: i })}
                  title="Punkt für diese Seite — Spiel und Satz rechnet der Server"
                >
                  + Punkt
                </button>
                <span className="deck-side__num" title="Punkte im laufenden Spiel">
                  {side.points}
                </span>
                <span className="deck-side__num" title="Spiele im Satz">
                  {side.games}
                </span>
                <span className="deck-side__num deck-side__num--big" title="Gewonnene Sätze">
                  {side.score}
                </span>
                <button
                  type="button"
                  className="deck-side__fix"
                  title="Ein Spiel abziehen (Korrektur)"
                  onClick={() => correct(i, 'games', Math.max(0, side.games - 1))}
                >
                  −
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="deck-side__fix"
                  title="Korrektur nach unten"
                  onClick={() => send({ t: 'ticker:score', side: i, delta: -1 })}
                >
                  −
                </button>
                <span className="deck-side__num deck-side__num--big">{side.score}</span>
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={() => {
                    send({
                      t: 'ticker:score',
                      side: i,
                      delta: 1,
                      text: note.trim() ? `${words.noun} für ${side.name} — ${note.trim()}.` : undefined,
                    });
                    setNote('');
                  }}
                >
                  {words.add}
                </button>
              </>
            )}
          </div>
        );
      })}

      {tennis ? (
        <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
          <span className="deck__hint" style={{ marginRight: 'auto' }}>{gameScoreLabel(ticker)}</span>
          <span className="deck__label" style={{ margin: 0 }}>Aufschlag</span>
          {([0, 1] as const).map((i) => (
            <button
              key={i}
              type="button"
              className={`chip${ticker.serving === i ? ' is-active' : ''}`}
              onClick={() => send({ t: 'ticker:board', patch: { serving: ticker.serving === i ? null : i } })}
            >
              {ticker.sides[i].short || ticker.sides[i].name}
            </button>
          ))}
        </div>
      ) : (
        <input
          className="deck__input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={words.hint}
          aria-label="Begleittext zum Zähler"
        />
      )}

      <PeriodChips ticker={ticker} send={send} />
    </DeckSection>
  );
}

function PeriodChips({ ticker, send }: { ticker: TickerState; send: (m: ClientMessage) => void }) {
  const options = PERIODS[ticker.mode] ?? [];
  return (
    <div className="chip-row">
      {options.map((p) => (
        <button
          key={p}
          type="button"
          className={`chip${ticker.period === p ? ' is-active' : ''}`}
          onClick={() => send({ t: 'ticker:board', patch: { period: ticker.period === p ? '' : p } })}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ Uhr */

/** Vorschlaege fuer die geplante Dauer, je nach Ticker-Art. */
const DURATIONS: Record<string, { label: string; seconds: number }[]> = {
  match: [
    { label: '2 × 45 min', seconds: 90 * 60 },
    { label: '2 × 30 min', seconds: 60 * 60 },
  ],
  race: [
    { label: '1 h', seconds: 60 * 60 },
    { label: '2 h', seconds: 2 * 3600 },
    { label: '4 h', seconds: 4 * 3600 },
  ],
  plain: [
    { label: '1 h', seconds: 60 * 60 },
    { label: '2 h', seconds: 2 * 3600 },
  ],
};

/**
 * Die Rueckfrage vor dem Stellen einer Backyard-Uhr: dort ist jede volle
 * Stunde ein Loop, die Uhr nimmt Loops und Strecke also mit. Gerechnet wie
 * auf dem Server (`backyardAtHour`) -- bei einem beendeten Event zaehlt er
 * nicht mehr, „Auf 0" faengt bei null Loops an. Fuehrt RACE RESULT die Loops
 * (`rrLoops`), zaehlt die Uhr nicht mit, sondern sichert nur nach unten ab
 * (`backyardFloorAtHour`) -- „Auf 0" laesst sie dann stehen.
 */
function clockQuestion(
  ticker: TickerState,
  next: number,
  reset: boolean,
  finished: boolean,
  rrLoops: boolean,
): string {
  const b = ticker.backyard;
  const hour = Math.floor(next / 3600);
  const after = finished
    ? b
    : reset
      ? rrLoops
        ? b
        : { ...b, loops: 0 }
      : rrLoops
        ? backyardFloorAtHour(b, hour)
        : backyardAtHour(b, hour);
  const km = (x: typeof b) => formatDistance(backyardTotalMeters(x));
  const effect =
    after.loops === b.loops
      ? `Loops und Strecke bleiben gleich (Loops: ${b.loops}, Strecke: ${km(b)}).`
      : `Damit ändern sich auch Loops und Strecke:\nLoops: ${b.loops} → ${after.loops}\nStrecke: ${km(b)} → ${km(after)}`;
  return `Uhr wirklich auf ${formatClock(next)} stellen?\n\n${effect}`;
}

/**
 * Uhr stellen statt nur zuruecksetzen.
 *
 * Das Eingabefeld nimmt `45`, `45:00` und `1:30:00` -- eine einzelne Zahl
 * sind Minuten, weil „stell auf 45" in der Regie nie 45 Sekunden meint. Die
 * geplante Dauer daneben stoppt nichts, sie gibt der Anzeige nur den Bezug:
 * aus „47:20" wird „47:20 von 1:10:00".
 */
function ClockPanel({
  ticker,
  finished,
  rrLoops,
  send,
}: {
  ticker: TickerState;
  finished: boolean;
  rrLoops: boolean;
  send: (m: ClientMessage) => void;
}) {
  const seconds = useTickerClock(ticker.clock);
  const [at, setAt] = useState('');
  const [duration, setDuration] = useState('');
  const target = ticker.clock.targetSeconds;

  /** Stellt die Uhr -- beim Backyard erst nach der Rueckfrage. Gibt zurueck, ob gestellt wurde. */
  const moveClock = (next: number, reset = false): boolean => {
    if (ticker.mode === 'backyard' && !window.confirm(clockQuestion(ticker, next, reset, finished, rrLoops))) {
      return false;
    }
    send(reset ? { t: 'ticker:clock', action: 'reset' } : { t: 'ticker:clock', action: 'set', seconds: next });
    return true;
  };

  const submitClock = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseClock(at);
    if (parsed === null) return;
    if (moveClock(parsed)) setAt('');
  };

  const submitDuration = (e: FormEvent) => {
    e.preventDefault();
    // Leeres Feld heisst „keine Dauer" -- der Server liest die 0 so.
    const parsed = duration.trim() ? parseClock(duration) : 0;
    if (parsed === null) return;
    send({ t: 'ticker:clock', action: 'target', seconds: parsed });
    setDuration('');
  };

  return (
    <DeckSection id="clock" title="Uhr" summary={<TickerClockLabel ticker={ticker} />}>

      <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
        <TickerClockLabel ticker={ticker} />
        <button
          type="button"
          className={`btn btn--sm${ticker.clock.running ? ' btn--onstage' : ''}`}
          disabled={finished && !ticker.clock.running}
          title={finished && !ticker.clock.running ? 'Das Event ist beendet — die Uhr bleibt stehen.' : undefined}
          onClick={() => send({ t: 'ticker:clock', action: ticker.clock.running ? 'stop' : 'start' })}
        >
          {ticker.clock.running ? 'Anhalten' : 'Starten'}
        </button>
        <button type="button" className="chip" onClick={() => moveClock(0, true)}>
          Auf 0
        </button>
        <button
          type="button"
          className="chip"
          title="Eine Minute zurück"
          onClick={() => moveClock(Math.max(0, seconds - 60))}
        >
          −1 min
        </button>
        <button
          type="button"
          className="chip"
          title="Eine Minute vor"
          onClick={() => moveClock(seconds + 60)}
        >
          +1 min
        </button>
      </div>

      <form className="deck-clock" onSubmit={submitClock}>
        <label htmlFor="clock-at">Uhr stellen</label>
        <input
          id="clock-at"
          value={at}
          onChange={(e) => setAt(e.target.value)}
          placeholder={formatClock(seconds)}
          inputMode="numeric"
        />
        <button className="btn btn--sm" type="submit" disabled={parseClock(at) === null}>
          Stellen
        </button>
      </form>

      <form className="deck-clock" onSubmit={submitDuration}>
        <label htmlFor="clock-duration">Dauer</label>
        <input
          id="clock-duration"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          placeholder={target !== null ? formatClock(target) : 'z. B. 1:10:00'}
          inputMode="numeric"
        />
        <button
          className="btn btn--sm btn--onstage"
          type="submit"
          disabled={!duration.trim() && target === null}
        >
          {duration.trim() ? 'Setzen' : 'Löschen'}
        </button>
      </form>

      <div className="chip-row">
        {(DURATIONS[ticker.mode] ?? []).map((d) => (
          <button
            key={d.label}
            type="button"
            className={`chip${target === d.seconds ? ' is-active' : ''}`}
            onClick={() => send({ t: 'ticker:clock', action: 'target', seconds: target === d.seconds ? 0 : d.seconds })}
          >
            {d.label}
          </button>
        ))}
        {ticker.mode === 'match' && (
          <button
            type="button"
            className="chip"
            title="Zweite Halbzeit: Uhr auf 45:00"
            onClick={() => send({ t: 'ticker:clock', action: 'set', seconds: 45 * 60 })}
          >
            Uhr auf 45:00
          </button>
        )}
      </div>
      <p className="deck__hint">
        Eingabe als <code>45</code> (Minuten), <code>45:00</code> oder <code>1:10:00</code>.
        {target !== null && ` Geplante Dauer: ${formatClock(target)}.`}
        {ticker.mode === 'backyard' &&
          ' Jede volle Stunde der Uhr ist ein Loop: Stellen ändert Loops und Strecke mit, vor wie zurück — deshalb kommt vorher eine Rückfrage. Geht bis 240 Stunden.'}
        {finished && ' Das Event ist beendet: Die Uhr bleibt auf ihrer Endzeit stehen, stellen lässt sie sich weiter.'}
      </p>
    </DeckSection>
  );
}

/* -------------------------------------------------------------- Strecke */

/* ------------------------------------------------------------ Backyard */

/** Ohne Backyard fuehrt RACE RESULT keinen Zaehler. */
const NO_RESULTS: ResultsBackyard = { field: false, loops: false, outage: false };

/**
 * Das Backyard-Pult: Rundenlaenge einmal, danach nur noch zaehlen.
 *
 * Im Rennen bedient das jemand mit einer Hand, waehrend die Laeufer
 * durchkommen -- deshalb sind Loops und Feld grosse +/- Knoepfe und keine
 * Eingabefelder. Die Rundenlaenge dagegen wird einmal gesetzt und danach
 * nicht mehr angefasst; sie darf ein Textfeld sein.
 *
 * Fuehrt RACE RESULT einen Zaehler (`rr`), steht sein Knopf still: eine
 * Korrektur hier hielte nur bis zur naechsten Abfrage und saehe dann aus, als
 * springe der Zaehler von selbst zurueck. Antwortet RACE RESULT nicht mehr
 * (`rr.outage`), sind beide wieder frei -- das ist der Notbetrieb, bis die
 * naechste Antwort kommt und wieder deren Zahlen gelten.
 */
function BackyardPanel({
  ticker,
  rr,
  lastOkAt,
  send,
}: {
  ticker: TickerState;
  rr: ResultsBackyard;
  lastOkAt: number | null;
  send: (m: ClientMessage) => void;
}) {
  const { lapMeters, loops, runners } = ticker.backyard;
  const [lap, setLap] = useState('');
  const [error, setError] = useState(false);

  /*
   * Was der Server bestaetigt hat, ist die Wahrheit -- auch wenn nebenan eine
   * zweite Regie sitzt. Waehrend des Tippens stoert das nicht, weil
   * `lapMeters` sich dann nicht aendert.
   *
   * Im Feld stehen immer Kilometer, auch bei einer 800-Meter-Runde (`0,8`).
   * `formatDistance` waere hier falsch: es schriebe „800", und beim naechsten
   * Speichern waeren daraus 800 Kilometer geworden.
   */
  useEffect(() => {
    setLap(new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(lapMeters / 1000));
    setError(false);
  }, [lapMeters]);

  const submitLap = (e: FormEvent) => {
    e.preventDefault();
    const meters = parseDistance(lap);
    if (meters === null) return setError(true);
    setError(false);
    send({ t: 'ticker:backyard', patch: { lapMeters: meters } });
  };

  const step = (patch: { loops?: number; runners?: number }) =>
    send({ t: 'ticker:backyard', patch });

  return (
    <DeckSection id="backyard" title="Backyard" count={formatDistance(backyardTotalMeters(ticker.backyard))}>

      <form className="form-inline" onSubmit={submitLap}>
        <input
          className={`deck__input${error ? ' is-bad' : ''}`}
          value={lap}
          onChange={(e) => setLap(e.target.value)}
          placeholder="6,7"
          aria-label="Länge einer Runde"
          inputMode="decimal"
        />
        <span className="deck__hint">km pro Loop</span>
        <button className="btn btn--sm btn--onstage" type="submit">
          Setzen
        </button>
      </form>

      <div className="deck-backyard">
        <Counter
          label="Loops"
          value={loops}
          disabled={rr.loops && !rr.outage}
          onStep={(delta) => step({ loops: Math.max(0, loops + delta) })}
        />
        <Counter
          label="im Rennen"
          value={runners}
          disabled={rr.field && !rr.outage}
          onStep={(delta) => step({ runners: Math.max(0, runners + delta) })}
        />
      </div>

      {rr.outage && (
        <div className="notice notice--stage">
          RACE RESULT antwortet nicht{lastOkAt ? ` (zuletzt ${timeShort.format(new Date(lastOkAt))})` : ''}.
          Solange zählt die Uhr die Loops zur vollen Stunde, und das Feld zählst du mit −/+. Sobald RACE RESULT
          wieder antwortet, gelten wieder dessen Zahlen.
        </div>
      )}

      <p className="deck__hint">
        {loops > 0 && `${formatDistance(lapMeters)} × ${loops} = ${formatDistance(backyardTotalMeters(ticker.backyard))}. `}
        {rr.outage
          ? ''
          : rr.loops
            ? 'Loops und Feld kommen aus RACE RESULT: ein Loop zählt, sobald alle im Rennen zurück sind — spätestens zur vollen Stunde der Uhr. Raus ist, wer dort DNF hat.'
            : rr.field
              ? 'Das Feld kommt aus RACE RESULT (alle ohne DNF). Die Liste hat keine Runden, deshalb zählen die Loops zu jeder vollen Stunde der Uhr +1 — mit −/+ korrigierst du von Hand.'
              : 'Loops zählen zu jeder vollen Stunde der Uhr von selbst +1 — mit −/+ korrigierst du von Hand.'}
      </p>
    </DeckSection>
  );
}

/** Ein Zaehler mit zwei grossen Knoepfen. */
function Counter({
  label,
  value,
  disabled = false,
  onStep,
}: {
  label: string;
  value: number;
  disabled?: boolean;
  onStep: (delta: number) => void;
}) {
  return (
    <div className="deck-counter">
      <span className="deck-counter__label">{label}</span>
      <div className="deck-counter__row">
        <button type="button" disabled={disabled} onClick={() => onStep(-1)} aria-label={`${label} eins weniger`}>
          −
        </button>
        <b>{value}</b>
        <button type="button" disabled={disabled} onClick={() => onStep(1)} aria-label={`${label} eins mehr`}>
          +
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------- Backyard: Strecke */

/** Woher die Rundenzeit in der Zeile kommt -- kurz, die Zeile ist schmal. */
function loopSourceLabel(p: CoursePosition): string {
  if (p.source === 'raceresult') return 'RR ⌀';
  if (p.source === 'measured') return p.laps > 1 ? `⌀ ${p.laps} Rd.` : '1 Rd.';
  return 'Standard';
}

/**
 * Die Streckenkarte der Regie: wo die Laeufer ungefaehr sind.
 *
 * Geschaetzt aus der Prognose jeder Person (`loopPrognosis`): die „Runde ⌀"
 * aus RACE RESULT, solange die Zeitnahme antwortet, sonst der Schnitt der
 * letzten drei Runden, die hier mit „zurück" gemessen wurden. Gepflegt wird
 * sie mit zwei Handgriffen: „zurück", wenn jemand von der Runde kommt, und
 * Nummer + Stelle antippen, wenn die Regie weiss, wo jemand gerade ist. Bis
 * zur ersten Messung gilt die Standardzeit.
 */
function CoursePanel({
  slug,
  ticker,
  course,
  results,
  spotBib,
  send,
}: {
  slug: string;
  ticker: TickerState;
  course: [number, number][] | null;
  results: ResultsStatus;
  spotBib: string | null;
  send: (m: ClientMessage) => void;
}) {
  const shape = useMemo(() => courseShape(course), [course]);
  const positions = useCoursePositions(ticker, results);
  const [selected, setSelected] = useState<string | null>(null);
  const [standard, setStandard] = useState('');
  const [error, setError] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const loopSeconds = ticker.backyard.loopSeconds ?? DEFAULT_LOOP_SECONDS;

  useEffect(() => {
    setStandard(formatClock(loopSeconds));
    setError(false);
  }, [loopSeconds]);

  // Fuer „Startliste übernehmen", solange noch niemand auf der Strecke steht.
  useEffect(() => {
    api
      .getEvent(slug)
      .then((r) => setParticipants(r.event.participants))
      .catch(() => undefined);
  }, [slug]);

  // Wer nicht mehr unterwegs ist, kann nicht mehr gewaehlt sein.
  useEffect(() => {
    if (selected && !positions.some((p) => p.entryId === selected)) setSelected(null);
  }, [positions, selected]);

  const submitStandard = (e: FormEvent) => {
    e.preventDefault();
    const seconds = parseClock(standard);
    if (seconds === null || seconds < MIN_LOOP_SECONDS || seconds > MAX_LOOP_SECONDS) return setError(true);
    setError(false);
    send({ t: 'ticker:backyard', patch: { loopSeconds: seconds } });
  };

  const missing = participants.filter((p) => !ticker.entries.some((e) => e.bib === p.bib));
  const takeStartlist = () => {
    for (const p of missing) send({ t: 'ticker:entry', entry: { bib: p.bib, name: p.name, status: 'onCourse' } });
  };

  const chosen = positions.find((p) => p.entryId === selected) ?? null;
  const rows = [...positions].sort((a, b) => a.bib.localeCompare(b.bib, 'de', { numeric: true }));
  const out = positions.filter((p) => !p.back).length;
  /*
   * Woher die Prognose kommt, in einem Satz -- vor allem, wenn RACE RESULT
   * eingeschaltet ist, aber gerade nicht gilt. Dann soll die Regie wissen,
   * dass ihre „zurück" jetzt die Punkte tragen.
   */
  const rrOn = results.config.source === 'raceresult';
  const rrPaced = positions.some((p) => p.source === 'raceresult');
  const paceNote = !rrOn
    ? null
    : !resultsPaceLive(results)
      ? 'RACE RESULT antwortet nicht — die Punkte laufen mit dem Schnitt der letzten drei „zurück“, sonst mit der Standardzeit.'
      : rrPaced
        ? 'Rundenzeiten aus RACE RESULT (Runde ⌀). Drück trotzdem „zurück“ — fällt die Zeitnahme aus, rechnet die Karte mit diesen Messungen weiter.'
        : 'RACE RESULT liefert keine Runde ⌀ — die Punkte laufen mit dem Schnitt der letzten drei „zurück“. Spalte prüfen unter „Ergebnisse“.';

  return (
    <DeckSection id="course" title="Strecke" count={`${out} unterwegs`}>
      {!shape ? (
        <p className="deck__hint">
          Für die Streckenkarte braucht das Event eine Strecke: in den Event-Einstellungen eine Strecken-Zone
          anlegen oder eine GPX-Datei laden.
        </p>
      ) : (
        <>
          <form className="form-inline" onSubmit={submitStandard}>
            <input
              className={`deck__input${error ? ' is-bad' : ''}`}
              value={standard}
              onChange={(e) => setStandard(e.target.value)}
              placeholder="50:00"
              aria-label="Standard-Rundenzeit"
              inputMode="numeric"
            />
            <span className="deck__hint">Standard-Rundenzeit</span>
            <button className="btn btn--sm btn--onstage" type="submit">
              Setzen
            </button>
          </form>

          <div className="course-desk">
            <CourseMap
              shape={shape}
              positions={positions}
              selectedId={selected}
              onSelect={(id) => setSelected((cur) => (cur === id ? null : id))}
              onPlace={(fraction) => {
                if (!selected) return;
                send({ t: 'ticker:course', entryId: selected, action: 'place', fraction });
                setSelected(null);
              }}
              className="course-map--desk"
              style={{ '--course-ratio': (shape.width + COURSE_PAD * 2) / (shape.height + COURSE_PAD * 2) } as CSSProperties}
            />
          </div>

          <p className="deck__hint">
            {chosen
              ? `Tippe auf die Strecke, wo Nr. ${chosen.bib || chosen.name} gerade ist.`
              : 'Kommt jemand von der Runde zurück: „zurück" — das misst die Runde, der Schnitt der letzten drei ist die Prognose. Weißt du, wo jemand ist: Nummer antippen, dann die Stelle auf der Strecke.'}
            {!ticker.clock.running && ' Die Uhr steht — die Punkte laufen erst mit ihr los.'}
          </p>
          {paceNote &&
            (rrPaced ? <p className="deck__hint">{paceNote}</p> : <div className="notice notice--stage">{paceNote}</div>)}

          {rows.length > 0 ? (
            <ul className="deck-course">
              {rows.map((p) => (
                <li key={p.entryId} className={p.entryId === selected ? 'is-selected' : undefined}>
                  <button
                    type="button"
                    className="bib"
                    aria-pressed={p.entryId === selected}
                    title="Wählen und dann auf der Strecke antippen, wo die Person gerade ist"
                    onClick={() => setSelected((cur) => (cur === p.entryId ? null : p.entryId))}
                  >
                    {p.bib || '—'}
                  </button>
                  <span className="deck-course__name">{p.name}</span>
                  <span className="deck-course__where">
                    {p.back ? 'zurück am Start' : `km ${formatKmShort(p.fraction * ticker.backyard.lapMeters)}`}
                    {' · '}
                    {formatClock(p.loopSeconds)} ({loopSourceLabel(p)})
                  </span>
                  <SpotlightButton bib={p.bib} activeBib={spotBib} send={send} />
                  <button
                    type="button"
                    className={`btn btn--sm${p.back ? ' btn--onstage' : ''}`}
                    onClick={() =>
                      send({ t: 'ticker:course', entryId: p.entryId, action: p.back ? 'unback' : 'back' })
                    }
                    title={p.back ? 'Doch noch nicht zurück — nimmt die Messung zurück' : 'Ist gerade von der Runde zurück'}
                  >
                    {p.back ? 'rückgängig' : 'zurück'}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="stack gap-8">
              <p className="deck__hint">
                Noch niemand unterwegs. Die Karte zeigt, wer unter „Auf der Strecke" als unterwegs eingetragen ist.
              </p>
              {missing.length > 0 && (
                <button className="btn btn--sm" type="button" style={{ alignSelf: 'flex-start' }} onClick={takeStartlist}>
                  Startliste übernehmen ({missing.length})
                </button>
              )}
            </div>
          )}
        </>
      )}
    </DeckSection>
  );
}

/** `3,2` -- Kilometer mit einer Stelle, so wie man es ansagt. */
const formatKmShort = (meters: number) =>
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(meters / 1000);

const RACE_STATUS: RaceEntryStatus[] = ['onCourse', 'finished', 'dnf'];

function RacePanel({
  slug,
  ticker,
  spotBib,
  send,
}: {
  slug: string;
  ticker: TickerState;
  /** Wessen Athleten-Pop-up gerade laeuft. */
  spotBib: string | null;
  send: (m: ClientMessage) => void;
}) {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [editing, setEditing] = useState<RaceEntry | null>(null);
  const [bib, setBib] = useState('');
  const [name, setName] = useState('');
  const [checkpoint, setCheckpoint] = useState('');
  const [time, setTime] = useState('');
  const [status, setStatus] = useState<RaceEntryStatus>('onCourse');
  const [place, setPlace] = useState('');

  // Nur zum Vorschlagen: die Startliste des Events.
  useEffect(() => {
    api
      .getEvent(slug)
      .then((r) => setParticipants(r.event.participants))
      .catch(() => undefined);
  }, [slug]);

  const byBib = useMemo(() => new Map(participants.map((p) => [p.bib, p])), [participants]);

  const reset = () => {
    setEditing(null);
    setBib('');
    setName('');
    setCheckpoint('');
    setTime('');
    setStatus('onCourse');
    setPlace('');
  };

  const load = (entry: RaceEntry) => {
    setEditing(entry);
    setBib(entry.bib);
    setName(entry.name);
    setCheckpoint(entry.checkpoint);
    setTime(entry.time);
    setStatus(entry.status);
    setPlace(entry.place ? String(entry.place) : '');
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!bib.trim() && !name.trim()) return;
    send({
      t: 'ticker:entry',
      entry: {
        id: editing?.id,
        bib: bib.trim(),
        // Steht die Nummer in der Startliste, nehmen wir den Namen von dort.
        name: name.trim() || byBib.get(bib.trim())?.name || '',
        checkpoint: checkpoint.trim(),
        time: time.trim(),
        status,
        place: status === 'finished' && place.trim() ? Number(place) : null,
      },
    });
    reset();
  };

  return (
    <DeckSection id="race" title="Auf der Strecke" count={ticker.entries.length}>

      <form className="deck-race" onSubmit={submit}>
        <input
          list="ticker-bibs"
          value={bib}
          onChange={(e) => {
            setBib(e.target.value);
            const hit = byBib.get(e.target.value.trim());
            if (hit && !name.trim()) setName(hit.name);
          }}
          placeholder="Nr."
          aria-label="Startnummer"
        />
        <datalist id="ticker-bibs">
          {participants.map((p) => (
            <option key={p.id} value={p.bib}>
              {p.name}
            </option>
          ))}
        </datalist>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" aria-label="Name" />
        <input
          value={checkpoint}
          onChange={(e) => setCheckpoint(e.target.value)}
          placeholder="km 5"
          aria-label="Messpunkt"
        />
        <input value={time} onChange={(e) => setTime(e.target.value)} placeholder="21:30" aria-label="Zeit" />
        <select value={status} onChange={(e) => setStatus(e.target.value as RaceEntryStatus)} aria-label="Status">
          {RACE_STATUS.map((s) => (
            <option key={s} value={s}>
              {raceStatusLabel[s]}
            </option>
          ))}
        </select>
        {status === 'finished' && (
          <input
            value={place}
            onChange={(e) => setPlace(e.target.value)}
            placeholder="Platz"
            inputMode="numeric"
            aria-label="Platz"
          />
        )}
        <div className="row gap-6">
          <button className="btn btn--sm" type="submit">
            {editing ? 'Übernehmen' : 'Eintragen'}
          </button>
          {editing && (
            <button className="btn btn--sm btn--onstage" type="button" onClick={reset}>
              Abbrechen
            </button>
          )}
        </div>
      </form>

      <ul className="deck-entries">
        {ticker.entries.map((e) => (
          <li key={e.id} className={`racelist__row--${e.status}`}>
            <span className="bib">{e.bib || '—'}</span>
            <span className="deck-entries__name">{e.name}</span>
            <span className="deck-entries__where">
              {e.status === 'finished' && e.place ? `Platz ${e.place}` : e.checkpoint || raceStatusLabel[e.status]}
              {e.time ? ` · ${e.time}` : ''}
            </span>
            <SpotlightButton bib={e.bib} activeBib={spotBib} send={send} />
            <button type="button" title="Bearbeiten" onClick={() => load(e)}>
              ✎
            </button>
            <button
              type="button"
              title="Aus der Liste nehmen"
              onClick={() => send({ t: 'ticker:entry:remove', entryId: e.id })}
            >
              ×
            </button>
          </li>
        ))}
        {ticker.entries.length === 0 && <li className="empty">Noch niemand eingetragen.</li>}
      </ul>

      <PeriodChips ticker={ticker} send={send} />
    </DeckSection>
  );
}

/* ------------------------------------------------------- Meldung & Feed */

function PostPanel({
  ticker,
  feed,
  send,
}: {
  ticker: TickerState;
  feed: TickerEntry[];
  send: (m: ClientMessage) => void;
}) {
  const [kind, setKind] = useState<TickerEntryKind>('note');
  const [mark, setMark] = useState('');
  const [text, setText] = useState('');

  const post = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    send({ t: 'ticker:post', kind, text: text.trim(), mark: mark.trim() || undefined });
    setText('');
    setMark('');
  };

  return (
    <DeckSection id="post" title="Meldung" count={feed.length}>

      <form className="deck-post" onSubmit={post}>
        <select value={kind} onChange={(e) => setKind(e.target.value as TickerEntryKind)} aria-label="Art">
          {TICKER_KINDS.map((k) => (
            <option key={k.id} value={k.id}>
              {k.icon} {k.label}
            </option>
          ))}
        </select>
        <input
          value={mark}
          onChange={(e) => setMark(e.target.value)}
          placeholder={ticker.mode === 'match' ? "67'" : 'Marke'}
          aria-label="Zeitmarke"
        />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Was ist passiert?"
          aria-label="Meldung"
        />
        <button className="btn btn--sm" type="submit" disabled={!text.trim()}>
          In den Ticker
        </button>
      </form>

      <div className="deck-feed">
        <TickerFeed
          entries={feed}
          limit={12}
          onRemove={(id) => send({ t: 'ticker:unpost', entryId: id })}
        />
      </div>
    </DeckSection>
  );
}

/* ------------------------------------------------------- Einblendungen */

function WidgetPanel({
  mode,
  note,
  widgets,
  spotlight,
  send,
}: {
  mode: TickerMode;
  note: string;
  widgets: WidgetState;
  spotlight: SpotlightState;
  send: (m: ClientMessage) => void;
}) {
  // Backyard-Anzeige und Streckenkarte zeigen ausserhalb ihres Modus nichts
  // an -- dann gehoeren sie auch nicht in die Liste der Schalter.
  const offered = WIDGETS.filter((w) => (w.id !== 'backyard' && w.id !== 'course') || mode === 'backyard');
  const active = offered.filter((w) => widgets[w.id].on).length;

  return (
    <DeckSection id="widgets" title="Einblendungen im Stream" count={`${active} an`}>
      <ul className="deck-widgets">
        {offered.map((w) => {
          const config = widgets[w.id];
          return (
            <li key={w.id}>
              <button
                type="button"
                className={`deck-widgets__on${config.on ? ' is-on' : ''}`}
                onClick={() => send({ t: 'widget:set', widget: w.id, on: !config.on })}
                title={w.id === 'course' ? `${w.hint} Vollbild an/aus: ${keyLabel('course')}` : w.hint}
              >
                {config.on ? 'an' : 'aus'}
              </button>
              <span className="deck-widgets__name" title={w.hint}>
                {w.label}
              </span>
              <WidgetColor
                label={w.label}
                color={config.color}
                commit={(hex) => send({ t: 'widget:set', widget: w.id, color: hex })}
              />
              {w.id === 'course' ? (
                <CoursePlacement config={config} send={send} />
              ) : (
                <select
                  value={config.slot}
                  onChange={(e) => send({ t: 'widget:set', widget: w.id, slot: e.target.value as never })}
                  aria-label={`Position ${w.label}`}
                >
                  {WIDGET_SLOTS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              )}
              {/* Groesse als Knopfreihe statt als Auswahlliste -- im Livebetrieb
                  will man einmal tippen, nicht aufklappen und suchen. */}
              <div className="deck-widgets__size" role="group" aria-label={`Größe ${w.label}`}>
                {WIDGET_SIZES.map((size) => (
                  <button
                    key={size.id}
                    type="button"
                    className={config.size === size.id ? 'is-on' : ''}
                    title={
                      w.id === 'course' && courseIsFull(config)
                        ? `Hauptbild in der Ecke: ${size.label}`
                        : `${w.label}: ${size.label}`
                    }
                    onClick={() => send({ t: 'widget:set', widget: w.id, size: size.id as WidgetSize })}
                  >
                    {size.short}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
        <SpotlightRow config={spotlight.config} send={send} />
        <SpotlightTourRow spotlight={spotlight} mode={mode} send={send} />
      </ul>
      <NoteField note={note} widgets={widgets} send={send} />
    </DeckSection>
  );
}

/**
 * Athleten-Pop-up: keine Einblendung zum Anschalten, sondern eine, die fuer
 * fuenf Sekunden kommt -- ausgeloest an der Startliste oder auf der Strecke.
 * Hier stehen nur Ecke, Groesse und Farbe, gleich fuer alle Pop-ups. Die Ecke
 * ist ein Wunsch: Logo, zweite Kamera und Hauptbild-Ecke haben Vorrang.
 */
function SpotlightRow({ config, send }: { config: SpotlightConfig; send: (m: ClientMessage) => void }) {
  const set = (patch: Extract<ClientMessage, { t: 'spotlight:config' }>['patch']) =>
    send({ t: 'spotlight:config', patch });
  return (
    <li>
      <span className="deck-widgets__on deck-widgets__on--info" title="Kommt für 5 Sekunden — ausgelöst in der Startliste, auf der Strecke oder per Durchgang">
        5 s
      </span>
      <span className="deck-widgets__name spot-row__name">
        <span title="Headshot, Name, Team, Info und Netzwerke — gepflegt in der Startliste">Athleten-Pop-up</span>
        <HelpLink topic="athleten-popup" />
      </span>
      <WidgetColor label="Athleten-Pop-up" color={config.color} commit={(hex) => set({ color: hex })} />
      <select
        value={config.slot}
        onChange={(e) => set({ slot: e.target.value as CornerSlot })}
        aria-label="Position Athleten-Pop-up"
      >
        {CORNER_SLOTS.map((s) => (
          <option key={s.id} value={s.id}>
            {s.label}
          </option>
        ))}
      </select>
      <div className="deck-widgets__size" role="group" aria-label="Größe Athleten-Pop-up">
        {WIDGET_SIZES.map((size) => (
          <button
            key={size.id}
            type="button"
            className={config.size === size.id ? 'is-on' : ''}
            title={`Athleten-Pop-up: ${size.label}`}
            onClick={() => set({ size: size.id })}
          >
            {size.short}
          </button>
        ))}
      </div>
    </li>
  );
}

/**
 * Durchgang des Athleten-Pop-ups: alle aus der Startliste oder nur, wer im
 * Rennen ist, nacheinander nach Startnummer -- statt jede Person einzeln
 * anzuklicken. Die Auswahl ist Teil der Pop-up-Einstellung und gilt damit
 * fuer jede Regie; den Ablauf fuehrt der Server (`spotlight:tour`).
 */
function SpotlightTourRow({
  spotlight,
  mode,
  send,
}: {
  spotlight: SpotlightState;
  mode: TickerMode;
  send: (m: ClientMessage) => void;
}) {
  const set = (patch: Extract<ClientMessage, { t: 'spotlight:config' }>['patch']) =>
    send({ t: 'spotlight:config', patch });
  const { who, empty, gap } = tourSettings(spotlight.config);
  // „Im Rennen" braucht eine Strecken-Liste -- ohne sie gilt „Alle".
  const racingOffered = showsRaceList(mode);
  const whoShown = racingOffered ? who : 'all';
  const tour = spotlight.tour;

  if (tour) {
    return (
      <li className="spot-tour is-running">
        <span className="spot-tour__state" role="status">
          <b>
            Durchgang {Math.max(1, tour.position)} von {tour.total}
          </b>
          {tour.current && (
            <span className="spot-tour__who">
              Nr. {tour.current.bib} {tour.current.name}
            </span>
          )}
          <TourNext tour={tour} />
        </span>
        <span className="spot-tour__actions">
          <button type="button" className="btn btn--sm btn--onstage" onClick={() => send({ t: 'spotlight:tour', action: 'skip' })}>
            Überspringen
          </button>
          <button type="button" className="btn btn--sm" onClick={() => send({ t: 'spotlight:tour', action: 'stop' })}>
            Stopp
          </button>
        </span>
      </li>
    );
  }

  const start = () => {
    if (whoShown !== who) set({ tourWho: whoShown });
    send({ t: 'spotlight:tour', action: 'start' });
  };
  return (
    <li className="spot-tour">
      <span className="spot-tour__title">Durchgang</span>
      <div className="deck-widgets__size spot-tour__pick" role="group" aria-label="Wer im Durchgang drankommt">
        {SPOTLIGHT_TOUR_WHO.map((w) => (
          <button
            key={w.id}
            type="button"
            className={whoShown === w.id ? 'is-on' : ''}
            disabled={w.id === 'racing' && !racingOffered}
            title={w.id === 'racing' && !racingOffered ? 'Nur mit dem Ticker Strecke oder Backyard' : w.hint}
            onClick={() => set({ tourWho: w.id })}
          >
            {w.label}
          </button>
        ))}
      </div>
      <select value={gap} onChange={(e) => set({ tourGap: Number(e.target.value) })} aria-label="Abstand im Durchgang">
        {SPOTLIGHT_TOUR_GAPS.map((g) => (
          <option key={g.seconds} value={g.seconds}>
            {g.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={`chip chip--sm${empty ? ' is-active' : ''}`}
        aria-pressed={empty}
        title="Ohne Häkchen kommt nur dran, wer ein Foto oder ein Netzwerk eingetragen hat"
        onClick={() => set({ tourEmpty: !empty })}
      >
        {empty ? '✓ ' : ''}auch ohne Foto/Netzwerk
      </button>
      <button type="button" className="btn btn--sm spot-tour__start" onClick={start}>
        ▶ Starten
      </button>
    </li>
  );
}

/** „nächste in 0:12" -- oder worauf der Durchgang gerade wartet. */
function TourNext({ tour }: { tour: SpotlightTour }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!tour.nextAt) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [tour.nextAt]);
  const text =
    tour.waiting === 'break'
      ? 'wartet, bis Werbepause oder Botschaft vorbei ist'
      : tour.waiting === 'manual'
        ? 'wartet, bis dein Pop-up weg ist'
        : tour.nextAt
          ? `nächste in ${formatClock(Math.max(0, Math.ceil((tour.nextAt - now) / 1000)))}`
          : tour.current
            ? 'letzte Person'
            : '';
  return text ? <span className="spot-tour__next">{text}</span> : null;
}

/**
 * Wo die Streckenkarte steht: im Vollbild -- dann mit der Ecke, in der das
 * Hauptbild klein weiterlaeuft -- oder als Kaestchen an einer der sechs
 * Stellen wie jede andere Einblendung. Eine Liste statt zweier Schalter: so
 * ist mit einem Griff beides gewaehlt.
 */
/** Die Ecke des Hauptbilds als Pfeil -- die Spalte ist schmal, „Vollbild · unten rechts" passte nicht. */
const CORNER_ARROWS: Record<string, string> = { topLeft: '↖', topRight: '↗', bottomLeft: '↙', bottomRight: '↘' };

function CoursePlacement({ config, send }: { config: WidgetConfig; send: (m: ClientMessage) => void }) {
  const full = courseIsFull(config);
  const value = full ? `full:${courseLiveSlot(config)}` : `card:${config.slot}`;
  return (
    <select
      value={value}
      onChange={(e) => {
        const [mode, slot] = e.target.value.split(':');
        send({ t: 'widget:set', widget: 'course', full: mode === 'full', slot: slot as WidgetSlot });
      }}
      aria-label="Position Strecke"
      title="Vollbild: die Karte füllt das Bild, das Hauptbild läuft klein in der gewählten Ecke weiter. Die Größe gilt dann dem Hauptbild."
    >
      <optgroup label="Vollbild — Hauptbild klein in der Ecke">
        {CORNER_SLOTS.map((s) => (
          <option key={s.id} value={`full:${s.id}`} title={`Vollbild, Hauptbild ${s.label}`}>
            Vollbild {CORNER_ARROWS[s.id]}
          </option>
        ))}
      </optgroup>
      <optgroup label="Kästchen">
        {WIDGET_SLOTS.map((s) => (
          <option key={s.id} value={`card:${s.id}`}>
            {s.label}
          </option>
        ))}
      </optgroup>
    </select>
  );
}

/**
 * Farbe einer Einblendung. „Standard" ist der bisherige Look (dunkel, rote
 * Akzente); jede Farbe toent das Kaestchen. Eine Vorgabe geht sofort raus;
 * das freie Farbfeld erst, wenn man kurz innehaelt -- sonst ginge jede
 * Zwischenfarbe beim Ziehen an alle Zuschauer und den RTMP-Ausgang. Bis dahin
 * zeigt der Knopf die Farbe, die gerade unter dem Finger liegt.
 */
function WidgetColor({
  label,
  color,
  commit,
}: {
  label: string;
  color: string | undefined;
  /** Farbe an den Server -- null heisst zurueck auf Standard. */
  commit: (hex: string | null) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  // Hat der Server die Farbe bestaetigt, gilt wieder sein Wert.
  useEffect(() => {
    if (draft && draft === color) setDraft(null);
  }, [color, draft]);

  return (
    <ColorPicker
      value={draft ?? color ?? null}
      title={`Farbe · ${label}`}
      label={`Farbe der Einblendung ${label}`}
      standard="Standard"
      onPick={(hex) => {
        window.clearTimeout(timer.current);
        setDraft(null);
        commit(hex);
      }}
      onCustom={(hex) => {
        setDraft(hex);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => commit(hex), 200);
      }}
    />
  );
}

/* --------------------------------------------------------------- Wetter */

/**
 * Wetter: welche Ansicht die Einblendung zeigt und fuer welchen Ort. Ohne
 * eigenen Ort gilt die Event-Zone, sonst die Ortsangabe des Events -- ein
 * eigener ist fuer Laeufe gedacht, deren Ziel woanders liegt als der Start.
 */
function WeatherPanel({
  weather,
  shown,
  send,
}: {
  weather: WeatherState;
  /** Ist die Einblendung an? Sonst ein Hinweis, wo man sie einschaltet. */
  shown: boolean;
  send: (m: ClientMessage) => void;
}) {
  const { config, report, error } = weather;
  const [place, setPlace] = useState(config.place ?? '');
  useEffect(() => setPlace(config.place ?? ''), [config.place]);
  const now = report ? weatherIcon(report.now.symbol) : null;
  const time = report
    ? new Intl.DateTimeFormat('de-DE', { timeZone: report.timeZone, hour: '2-digit', minute: '2-digit' }).format(report.fetchedAt)
    : '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    send({ t: 'weather:set', patch: { place: place.trim() || null } });
  };

  return (
    <DeckSection
      id="weather"
      title="Wetter"
      summary={report && now ? `${now.icon} ${formatTemp(report.now.temp)} · ${report.place}` : undefined}
    >
      <div className="chip-row">
        {WEATHER_VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className={`chip${config.view === v.id ? ' is-active' : ''}`}
            title={v.hint}
            onClick={() => send({ t: 'weather:set', patch: { view: v.id } })}
          >
            {v.label}
          </button>
        ))}
      </div>
      <form className="deck-clock" onSubmit={submit}>
        <label htmlFor="weather-place">Ort</label>
        <input
          id="weather-place"
          value={place}
          maxLength={80}
          onChange={(e) => setPlace(e.target.value)}
          placeholder={config.place ? '' : `Event-Ort${report ? ` (${report.place})` : ''}`}
        />
        <button className="btn btn--sm btn--onstage" type="submit" disabled={(place.trim() || null) === config.place}>
          {/* Leer und ein eigener Ort gesetzt: zurueck zum Ort des Events. */}
          {!place.trim() && config.place ? 'Event-Ort' : 'Setzen'}
        </button>
      </form>
      {error && <div className="notice notice--stage">{error}</div>}
      <p className="deck__hint">
        {report && now
          ? `${report.place}, Stand ${time} Uhr: jetzt ${formatTemp(report.now.temp)}, ${now.text}, Wind ${report.now.windKmh} km/h. `
          : 'Die Vorhersage wird geholt … '}
        {shown ? '' : 'Ins Bild kommt sie über „Wetter“ bei den Einblendungen. '}
        {WEATHER_CREDIT} (CC BY 4.0), alle 30 Minuten neu.
      </p>
    </DeckSection>
  );
}

/* ------------------------------------------------------ Abschnitte im Pult */

const FOLD_KEY = 'rabbitfuel:regie-pult';

function readFolds(): Record<string, boolean> {
  try {
    return (JSON.parse(localStorage.getItem(FOLD_KEY) ?? '{}') as Record<string, boolean>) ?? {};
  } catch {
    return {};
  }
}

/**
 * Ein Abschnitt des Pults, den die Ueberschrift auf- und zuklappt. Der
 * Browser merkt sich das je Abschnitt -- wer beim Backyard nur die Zaehler
 * braucht, klappt den Rest einmal weg und findet ihn beim naechsten Event so
 * wieder. Zugeklappt steht das Wichtigste trotzdem in der Zeile (`summary`).
 */
function DeckSection({
  id,
  title,
  count,
  summary,
  children,
}: {
  id: string;
  title: ReactNode;
  /** Steht immer neben dem Titel. */
  count?: ReactNode;
  /** Steht nur zugeklappt am rechten Rand -- aufgeklappt sieht man es ohnehin. */
  summary?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(() => readFolds()[id] !== false);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(FOLD_KEY, JSON.stringify({ ...readFolds(), [id]: next }));
    } catch {
      /* Privater Modus -- dann eben nur bis zum Neuladen. */
    }
  };

  return (
    <div className={`deck__block${open ? '' : ' is-folded'}`}>
      <button type="button" className="deck__label deck__fold" onClick={toggle} aria-expanded={open}>
        <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden>
          <path
            d={open ? 'M3 5.5l4 4 4-4' : 'M5.5 3l4 4-4 4'}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {title}
        {count !== undefined && <span className="deck__count">{count}</span>}
        {!open && summary !== undefined && <span className="deck__summary">{summary}</span>}
      </button>
      {open && children}
    </div>
  );
}

/** Der freie Text hinter dem Notiz-Widget. */
function NoteField({
  note,
  widgets,
  send,
}: {
  note: string;
  widgets: WidgetState;
  send: (m: ClientMessage) => void;
}) {
  const [draft, setDraft] = useState(note);
  // Nachziehen, sobald der Server den Text bestaetigt oder eine zweite Regie
  // ihn aendert -- waehrend des Tippens bleibt `note` gleich und stoert nicht.
  useEffect(() => setDraft(note), [note]);

  return (
    <form
      className="form-inline"
      onSubmit={(e) => {
        e.preventDefault();
        send({ t: 'ticker:board', patch: { note: draft.trim() } });
        if (!widgets.note.on && draft.trim()) send({ t: 'widget:set', widget: 'note', on: true });
      }}
    >
      <input
        className="deck__input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Text für die Notiz-Einblendung"
        aria-label="Notiztext"
      />
      <button className="btn btn--sm btn--onstage" type="submit">
        Setzen
      </button>
    </form>
  );
}
